<template>
  <div class="n8n-instagram-accounts">
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h3>{{ $t('n8n.instagram_accounts') }}</h3>
        <b-button
          variant="primary"
          @click="openCreateModal"
        >
          <feather-icon icon="PlusIcon" />
          {{ $t('forms.add') }}
        </b-button>
      </div>

      <ok-table
        ref="datatable"
        :config="config"
        class="n8n-instagram-accounts-table"
        @edit="handleEdit"
        @delete="handleDelete"
        @history="handleHistory"
      >
        <template
          slot="status-column"
          slot-scope="{props}"
        >
          <b-badge
            :variant="getStatusBadgeVariant(props.row.status)"
          >
            {{ getStatusText(props.row.status) }}
          </b-badge>
        </template>
        <template
          slot="start_at-column"
          slot-scope="{props}"
        >
          <span v-if="props.row.start_at">
            {{ formatDate(props.row.start_at) }}
          </span>
          <span v-else>
            -
          </span>
        </template>
        <template
          slot="expires_at-column"
          slot-scope="{props}"
        >
          <span v-if="props.row.expires_at">
            {{ formatDate(props.row.expires_at) }}
          </span>
          <span v-else>
            -
          </span>
        </template>
        <template
          slot="execute_at-column"
          slot-scope="{props}"
        >
          <span v-if="props.row.execute_at">
            {{ props.row.execute_at }}
          </span>
          <span v-else>
            -
          </span>
        </template>
      </ok-table>
    </b-card>

    <!-- Create/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? $t('forms.edit') : $t('forms.add')"
      ok-title="forms.submit"
      cancel-title="forms.cancel"
      @ok="handleSubmit"
      @hidden="resetForm"
    >
      <validation-observer ref="formObserver">
        <b-form>
          <b-form-group
            :label="$t('instagram.account')"
            label-for="instagram_account_id"
          >
            <validation-provider
              #default="{ errors }"
              name="instagram_account_id"
              rules="required"
            >
              <v-select
                id="instagram_account_id"
                v-model="formData.instagram_account_id"
                :options="instagramAccountsOptions"
                :reduce="account => account.id"
                label="displayName"
                :placeholder="$t('instagram.select-account')"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('n8n.start_at')"
            label-for="start_at"
          >
            <validation-provider
              #default="{ errors }"
              name="start_at"
            >
              <flat-pickr
                id="start_at"
                v-model="formData.start_at"
                class="form-control"
                :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
                :placeholder="$t('n8n.select-start-date')"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('n8n.expires_at')"
            label-for="expires_at"
          >
            <validation-provider
              #default="{ errors }"
              name="expires_at"
            >
              <flat-pickr
                id="expires_at"
                v-model="formData.expires_at"
                class="form-control"
                :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
                :placeholder="$t('n8n.select-expire-date')"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('n8n.post_count')"
            label-for="post_count"
          >
            <validation-provider
              #default="{ errors }"
              name="post_count"
              rules="min:0"
            >
              <b-form-input
                id="post_count"
                v-model.number="formData.post_count"
                type="number"
                min="0"
                :placeholder="$t('n8n.enter-post-count')"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('n8n.status')"
            label-for="status"
          >
            <b-form-select
              id="status"
              v-model="formData.status"
              :options="statusOptions"
            />
          </b-form-group>

          <b-form-group
            :label="$t('n8n.ai_prompt')"
            label-for="ai_prompt"
          >
            <b-form-textarea
              id="ai_prompt"
              v-model="formData.ai_prompt"
              rows="3"
              :placeholder="$t('n8n.enter-ai-prompt')"
            />
          </b-form-group>

          <b-form-group
            :label="$t('n8n.telegram_chat_id')"
            label-for="telegram_chat_id"
          >
            <b-form-input
              id="telegram_chat_id"
              v-model="formData.telegram_chat_id"
              :placeholder="$t('n8n.enter-telegram-chat-id')"
            />
          </b-form-group>

          <b-form-group
            :label="$t('n8n.execute_at')"
            label-for="execute_at"
          >
            <flat-pickr
              id="execute_at"
              v-model="formData.execute_at"
              class="form-control"
              :config="{ enableTime: true, noCalendar: true, dateFormat: 'H:i' }"
              :placeholder="$t('n8n.select-execute-time')"
            />
          </b-form-group>
        </b-form>
      </validation-observer>
    </b-modal>

    <!-- History Modal -->
    <b-modal
      v-model="showHistoryModal"
      :title="$t('n8n.history')"
      size="lg"
      ok-only
    >
      <div v-if="historyLoading">
        <div class="text-center py-3">
          <b-spinner variant="primary" />
        </div>
      </div>
      <div v-else-if="historyItems.length > 0">
        <b-table
          :items="historyItems"
          :fields="historyFields"
          responsive
        >
          <template #cell(status)="data">
            <b-badge
              :variant="getStatusVariant(data.item.status)"
            >
              {{ $t(`n8n.${data.item.status}`) }}
            </b-badge>
          </template>
          <template #cell(error)="data">
            <span v-if="data.item.error">
              {{ data.item.error }}
            </span>
            <span v-else>
              -
            </span>
          </template>
        </b-table>
      </div>
      <div v-else>
        <div class="text-center py-3">
          {{ $t('n8n.no-history') }}
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BModal, BSpinner, BTable,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import OkTable from '@/@bya3/components/tables/ok-table/OkTable'
import N8nInstagramAccountsApi from '@/api/n8n-instagram-accounts'
import InstagramAccountsApi from '@/api/instagram/account'
import N8nInstagramAccountsTableConfig from './config/n8n-instagram-accounts-table-config'

export default {
  components: {
    BCard,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BTable,
    BSpinner,
    BBadge,
    OkTable,
    vSelect,
    flatPickr,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      config: N8nInstagramAccountsTableConfig,
      showModal: false,
      showHistoryModal: false,
      isEdit: false,
      historyLoading: false,
      historyItems: [],
      historyFields: [
        { key: 'id', label: 'fields.id' },
        { key: 'post_id', label: 'n8n.post_id' },
        { key: 'reply_id', label: 'n8n.reply_id' },
        { key: 'topic', label: 'n8n.topic' },
        { key: 'status', label: 'n8n.status' },
        { key: 'error', label: 'n8n.error' },
        { key: 'created_at', label: 'fields.created_at' },
      ],
      formData: {
        instagram_account_id: null,
        start_at: null,
        expires_at: null,
        post_count: 0,
        status: 0,
        ai_prompt: '',
        telegram_chat_id: '',
        execute_at: null,
      },
      statusOptions: [
        { value: 0, text: 'Pending' },
        { value: 1, text: 'Active' },
        { value: 2, text: 'Processing' },
      ],
      instagramAccountsOptions: [],
      selectedN8nInstagramAccount: null,
    }
  },
  async mounted() {
    await this.loadInstagramAccounts()
  },
  methods: {
    async loadInstagramAccounts() {
      try {
        const response = await InstagramAccountsApi.getAccounts()
        let list = []
        if (Array.isArray(response?.accounts)) {
          list = response.accounts
        } else if (Array.isArray(response?.data)) {
          list = response.data
        }
        this.instagramAccountsOptions = list.map(a => ({
          ...a,
          displayName: String(
            a.username || a.name || a.account_id || a.id || '',
          ).trim() || `#${a.id}`,
        }))
      } catch (error) {
        this.$notifyError(error)
      }
    },
    openCreateModal() {
      this.isEdit = false
      this.selectedN8nInstagramAccount = null
      this.resetForm()
      this.showModal = true
    },
    handleEdit(props) {
      this.isEdit = true
      this.selectedN8nInstagramAccount = props.row
      this.formData = {
        instagram_account_id: props.row.instagram_account_id,
        start_at: props.row.start_at,
        expires_at: props.row.expires_at,
        post_count: props.row.post_count,
        status: props.row.status,
        ai_prompt: props.row.ai_prompt || '',
        telegram_chat_id: props.row.telegram_chat_id || '',
        execute_at: props.row.execute_at,
      }
      this.showModal = true
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()

      const valid = await this.$refs.formObserver.validate()
      if (!valid) return

      try {
        if (this.isEdit && this.selectedN8nInstagramAccount) {
          await N8nInstagramAccountsApi.updateN8nInstagramAccount(this.selectedN8nInstagramAccount.id, this.formData)
          this.$notifySuccess(this.$t('n8n.updated-successfully'))
        } else {
          await N8nInstagramAccountsApi.createN8nInstagramAccount(this.formData)
          this.$notifySuccess(this.$t('n8n.created-successfully'))
        }
        this.showModal = false
        this.refreshTable()
      } catch (error) {
        this.$notifyError(error)
      }
    },
    handleDelete(props) {
      this.$okConfirm(this.$t('n8n.delete-confirm'), {
        preConfirm: () => N8nInstagramAccountsApi.deleteN8nInstagramAccount(props.row.id)
          .then(() => {
            this.$notifySuccess(this.$t('n8n.deleted-successfully'))
            this.refreshTable()
          })
          .catch(e => this.$notifyError(e)),
      })
    },
    async handleHistory(props) {
      this.selectedN8nInstagramAccount = props.row
      this.historyLoading = true
      this.showHistoryModal = true

      try {
        const response = await N8nInstagramAccountsApi.getN8nInstagramAccountsHistory({ n8n_instagram_account_id: props.row.id })
        this.historyItems = response.data || []
      } catch (error) {
        this.$notifyError(error)
        this.historyItems = []
      } finally {
        this.historyLoading = false
      }
    },
    refreshTable() {
      this.$refs.datatable.loadItems()
    },
    resetForm() {
      this.formData = {
        instagram_account_id: null,
        start_at: null,
        expires_at: null,
        post_count: 0,
        status: 0,
        ai_prompt: '',
        telegram_chat_id: '',
        execute_at: null,
      }
      if (this.$refs.formObserver) {
        this.$refs.formObserver.reset()
      }
    },
    formatDate(date) {
      if (!date) return '-'
      return this.$moment(date).format('YYYY-MM-DD HH:mm')
    },
    getStatusVariant(status) {
      switch (status) {
        case 'completed':
          return 'success'
        case 'failed':
          return 'danger'
        case 'pending':
        default:
          return 'warning'
      }
    },
    getStatusBadgeVariant(status) {
      switch (status) {
        case 1:
          return 'success'
        case 2:
          return 'warning'
        case 0:
        default:
          return 'danger'
      }
    },
    getStatusText(status) {
      switch (status) {
        case 1:
          return 'Active'
        case 2:
          return 'Processing'
        case 0:
        default:
          return 'Pending'
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
